import { Link } from "react-router-dom";
import {
    Users,
    CalendarCheck,
    CalendarClock,
    UserPlus,
    ListChecks,
    LineChart,
    ArrowRight,
    Music2,
    CalendarDays,
    Shield,
    UserCheck,
    User,
    Zap,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { ROLES, isActive } from "../lib/auth";
import StatCard from "../components/StatCard";
import TodaySchedule from "../components/TodaySchedule";
import QuickLogModal from "../components/QuickLogModal";
import NeedsAttentionCard from "../components/NeedsAttentionCard";
import SessionsTodayCard from "../components/dashboard/SessionsTodayCard";
import StatusChip from "../components/dashboard/StatusChip";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { todayStr, formatDateShort, formatRelativeTime } from "../lib/progress";
import { todayDay, getTodaysStudents } from "../lib/schedule";
import { accentBadgeHover } from "../lib/accentTokens";

const QUICK = [
    {
        to: "/students/new",
        label: "Add Student",
        icon: UserPlus,
        accent: "orange",
        testId: "qa-add-student",
    },
    {
        to: "/students",
        label: "View Students",
        icon: Users,
        accent: "blue",
        testId: "qa-view-students",
    },
    {
        to: "/makeups",
        label: "Make-up Summary",
        icon: ListChecks,
        accent: "amber",
        testId: "qa-view-makeups",
    },
    {
        to: "/reports",
        label: "View Reports",
        icon: LineChart,
        accent: "green",
        testId: "qa-view-reports",
    },
];

export default function Dashboard() {
    const { students, attendance, allStudents, allAttendance } = useData();
    const { isAdmin, workspaceMode, teachers, setWorkspaceMode } = useAuth();
    const today = todayStr();
    const day = todayDay();

    const isStudioView = isAdmin && workspaceMode === "all";

    const activeTeacherUsers = teachers.filter(
        (t) => t.role === ROLES.TEACHER && isActive(t),
    );
    const selectedTeacher =
        isAdmin && workspaceMode !== "all"
            ? teachers.find((t) => t.id === workspaceMode)
            : null;

    const sessionsToday = attendance.filter((r) => r.date === today).length;
    const pendingMakeups = attendance.filter(
        (r) => r.status === "Excused" && !(r.remark || "").startsWith("Done"),
    );
    const todaysStudents = getTodaysStudents(students, day);
    const loggedStudentIds = new Set(
        attendance.filter((r) => r.date === today).map((r) => r.studentId),
    );
    const remainingToday = todaysStudents.filter(
        (r) => !loggedStudentIds.has(r.student.id),
    ).length;

    const totalTeachers = teachers.filter((t) => t.role === ROLES.TEACHER).length;
    const activeTeachersCount = teachers.filter(
        (t) => t.role === ROLES.TEACHER && isActive(t),
    ).length;

    const recentActivity = [...attendance]
        .sort((a, b) =>
            (b.updatedAt || b.createdAt).localeCompare(a.updatedAt || a.createdAt),
        )
        .slice(0, 5);

    return (
        <div className="space-y-10" data-testid="dashboard-page">
            {/* === Hero — only for actual teachers (not admin) === */}
            {!isAdmin && (
                <section
                    className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-brand-soft/60 rounded-3xl border border-mist p-8 sm:p-10 grain"
                    data-testid="dashboard-hero"
                >
                    <div className="relative z-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-mist rounded-full px-3 py-1 mb-4">
                                <Music2
                                    size={14}
                                    className="text-slate-500"
                                    strokeWidth={2.8}
                                />
                                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                                    Today ·{" "}
                                    {new Date().toLocaleDateString(undefined, {
                                        weekday: "long",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </span>
                            </div>
                            <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight text-slate-900 leading-[0.95]">
                                Good to see you.<br />
                                <span className="text-slate-700">
                                    Ready to log{" "}
                                    <span className="text-[#3D6385]">today’s lessons</span>
                                    ?
                                </span>
                            </h1>
                            <p className="mt-4 text-slate-600 max-w-xl">
                                Keep every lesson accounted for, monitor monthly progress,
                                and clear pending make-ups in a few clicks.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <QuickLogModal
                                triggerClassName="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-12 px-6 shadow-md"
                                triggerLabel="Log Today's Attendance"
                            />
                            <Link to="/students/new" data-testid="hero-add-student-btn">
                                <Button
                                    variant="outline"
                                    className="rounded-xl h-12 px-6 font-bold border-mist bg-white/70 backdrop-blur hover:bg-white w-full sm:w-auto"
                                >
                                    <UserPlus
                                        size={18}
                                        strokeWidth={2.8}
                                        className="mr-2"
                                    />
                                    Add Student
                                </Button>
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* === SECTION 1 — Studio Overview (Admin only) === */}
            {isAdmin && (
                <section
                    className="space-y-5"
                    data-testid="studio-overview-section"
                >
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-1">
                                <Shield size={11} strokeWidth={3} />
                                Admin View
                            </div>
                            <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight">
                                Studio Overview
                            </h1>
                            <p className="text-slate-500 mt-2 max-w-xl">
                                Studio-wide metrics across all teacher workspaces.
                            </p>
                        </div>
                        <div className="w-full sm:w-72 space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 inline-flex items-center gap-1">
                                <Users size={12} strokeWidth={3} />
                                Viewing Teacher
                            </label>
                            <Select
                                value={workspaceMode}
                                onValueChange={setWorkspaceMode}
                            >
                                <SelectTrigger
                                    className="h-12 rounded-xl bg-white border-mist font-bold"
                                    data-testid="dashboard-teacher-filter"
                                >
                                    <SelectValue placeholder="All Teachers" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem
                                        value="all"
                                        data-testid="dashboard-filter-all"
                                    >
                                        All Teachers
                                    </SelectItem>
                                    {activeTeacherUsers.map((t) => (
                                        <SelectItem
                                            key={t.id}
                                            value={t.id}
                                            data-testid={`dashboard-filter-${t.id}`}
                                        >
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div
                        className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5"
                        data-testid="studio-stats"
                    >
                        <StatCard
                            label="Total Teachers"
                            value={totalTeachers}
                            icon={Users}
                            accent="blue"
                            to="/admin/teachers"
                            testId="stat-total-teachers"
                        />
                        <StatCard
                            label="Active Teachers"
                            value={activeTeachersCount}
                            icon={UserCheck}
                            accent="green"
                            to="/admin/teachers"
                            testId="stat-active-teachers"
                        />
                        <StatCard
                            label="Total Students"
                            value={allStudents.length}
                            icon={Users}
                            accent="blue"
                            to="/students"
                            testId="stat-studio-students"
                        />
                        <StatCard
                            label="Sessions Today"
                            value={
                                allAttendance.filter((r) => r.date === today).length
                            }
                            icon={CalendarCheck}
                            accent="blue"
                            testId="stat-studio-sessions"
                        />
                        <StatCard
                            label="Pending Make-ups"
                            value={
                                allAttendance.filter(
                                    (r) =>
                                        r.status === "Excused" &&
                                        !(r.remark || "").startsWith("Done"),
                                ).length
                            }
                            icon={CalendarClock}
                            accent="amber"
                            to="/makeups"
                            testId="stat-studio-makeups"
                        />
                    </div>
                </section>
            )}

            {/* === SECTION 2 — Teacher Workspace Snapshot ===
                Visible for: (a) teachers in their own view,
                             (b) admin viewing a specific teacher.
                Hidden when admin has "All Teachers" selected. */}
            {!isStudioView && (
                <>
                    {isAdmin && selectedTeacher && (
                        <section data-testid="teacher-snapshot-header">
                            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-1">
                                <User size={11} strokeWidth={3} />
                                Selected Workspace
                            </div>
                            <h2 className="font-display font-bold text-3xl sm:text-4xl uppercase tracking-tight">
                                {selectedTeacher.name}'s Snapshot
                            </h2>
                            <p className="text-slate-500 mt-2 max-w-xl text-sm">
                                Teacher-specific metrics, today's schedule, and recent
                                activity.
                            </p>
                        </section>
                    )}

                    {/* 3-card scoped stats */}
                    <section className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
                        <StatCard
                            label="Total Students"
                            value={students.length}
                            hint={`${students.filter((s) => s.type === "Local").length} local · ${students.filter((s) => s.type === "Regional").length} regional · ${students.filter((s) => s.type === "International").length} intl`}
                            icon={Users}
                            accent="blue"
                            to="/students"
                            testId="stat-total-students"
                        />
                        <SessionsTodayCard count={sessionsToday} />
                        <StatCard
                            label="Pending Make-ups"
                            value={pendingMakeups.length}
                            hint={
                                pendingMakeups.length
                                    ? "Needs follow-up"
                                    : "All caught up"
                            }
                            icon={CalendarClock}
                            accent="amber"
                            to="/makeups"
                            testId="stat-pending-makeups"
                        />
                    </section>

                    {/* Needs Attention */}
                    <NeedsAttentionCard />

                    {/* Today's Schedule */}
                    <section
                        className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8"
                        data-testid="today-schedule-section"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-9 h-9 rounded-xl bg-action-soft text-[#3D6385] flex items-center justify-center">
                                        <CalendarDays size={18} strokeWidth={2.5} />
                                    </div>
                                    <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
                                        Today's Lessons
                                    </h2>
                                </div>
                                <p className="text-sm text-slate-500">
                                    {todaysStudents.length === 0
                                        ? students.length === 0
                                            ? "No students yet. Add your first student to get started."
                                            : `No lessons scheduled for ${day}.`
                                        : remainingToday === 0
                                          ? "All of today’s students are logged. Nice work."
                                          : `${remainingToday} of ${todaysStudents.length} still needs attendance`}
                                </p>
                                {todaysStudents.length > 0 && remainingToday > 0 && (
                                    <p className="text-xs font-bold text-slate-600 mt-2 inline-flex items-center gap-1.5">
                                        <Zap
                                            size={12}
                                            strokeWidth={2.8}
                                            className="text-[#3D6385] fill-[#3D6385]"
                                        />
                                        Quick log all before class starts
                                    </p>
                                )}
                            </div>
                            {todaysStudents.length > 0 && (
                                <QuickLogModal
                                    triggerClassName="rounded-xl bg-ink hover:bg-ink/90 text-white font-bold h-11 px-5 shadow-md"
                                    triggerLabel="Open Quick Log"
                                />
                            )}
                        </div>
                        <TodaySchedule />
                    </section>

                    {/* Quick actions + activity */}
                    <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
                                    Quick Actions
                                </h2>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                {QUICK.map((q) => (
                                    <Link
                                        key={q.to}
                                        to={q.to}
                                        data-testid={q.testId}
                                        className="group flex items-center gap-4 p-4 sm:p-5 rounded-2xl border border-mist hover:border-mist hover:-translate-y-0.5 transition-all"
                                    >
                                        <div
                                            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${accentBadgeHover[q.accent]}`}
                                        >
                                            <q.icon size={20} strokeWidth={2.5} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-900 text-sm sm:text-base">
                                                {q.label}
                                            </div>
                                        </div>
                                        <ArrowRight
                                            size={18}
                                            className="text-slate-300 group-hover:text-slate-500 transition-colors"
                                            strokeWidth={2.5}
                                        />
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8">
                            <div className="flex items-center justify-between mb-5">
                                <h2 className="font-display font-bold text-xl uppercase tracking-tight">
                                    Recent Activity
                                </h2>
                            </div>
                            {recentActivity.length === 0 ? (
                                <div className="space-y-3">
                                    <div className="text-sm text-slate-500">
                                        No attendance yet. Start by logging a session.
                                    </div>
                                    <QuickLogModal
                                        triggerClassName="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-10 px-4 text-sm"
                                        triggerLabel="Log Today's Attendance"
                                    />
                                </div>
                            ) : (
                                <ul
                                    className="space-y-1"
                                    data-testid="recent-activity-list"
                                >
                                    {recentActivity.map((r) => (
                                        <li key={r.id}>
                                            <Link
                                                to={`/students/${r.studentId}`}
                                                data-testid={`recent-activity-${r.id}`}
                                                className="flex items-center justify-between gap-3 py-2.5 px-2 -mx-2 rounded-xl hover:bg-mist/50 transition-colors border-b last:border-0 border-mist"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-bold text-sm text-slate-800 truncate">
                                                        {r.studentName}
                                                    </div>
                                                    <div className="text-xs text-slate-500 flex items-center gap-1.5">
                                                        <span>{formatDateShort(r.date)}</span>
                                                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                                                        <span
                                                            data-testid={`recent-activity-time-${r.id}`}
                                                        >
                                                            {formatRelativeTime(
                                                                r.updatedAt || r.createdAt,
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                <StatusChip status={r.status} />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
